/**
 * SAM-198 — recurring class schedules: a weekday can have more than one
 * booking window (e.g. Yoga Monday 08:00-09:00 AND 18:00-19:00). Capacity
 * stays uniform across windows (the owner's decision, no per-window field).
 *
 * Wire shape (fixed by the backend, see ServiceDetailDrawer.tsx):
 *   "monday": {"open": "08:00", "close": "09:00", "enabled": true,
 *              "extra_windows": [{"open": "18:00", "close": "19:00"}]}
 *
 * `open`/`close` stay the FIRST window. `extra_windows` is optional; a day
 * with none must omit the key entirely so unrelated services keep a
 * byte-identical payload.
 *
 * The FIRST window is allowed to cross midnight (close <= open), same as
 * before this feature existed — e.g. a bar open 18:00 -> 02:00. The
 * backend's `weekday_windows()` (mirrored by useServices.computeWeekdayWindows)
 * splits that into "tonight's tail" and "tomorrow's head". A day with no
 * extra windows is therefore not validated at all here, exactly today's
 * behaviour, whatever its open/close are. Extra windows themselves must NOT
 * cross midnight — they're plain same-day windows.
 *
 * Everything here is pure so it can be unit tested without mounting the
 * (grandfathered BaseUI/Styletron) drawer.
 */

export interface TimeWindow {
  open: string;
  close: string;
  // SAM-546 — Sessions editor (src/pages/ServiceEditor/sections/SessionsSection.tsx).
  // Free-text label-only metadata: an admin-facing name/cupo/instructor for THIS
  // specific window, so "Yoga Monday 08:00-09:00" and "Yoga Monday 18:00-19:00" can
  // read as two distinct sessions instead of two anonymous time ranges.
  //
  // None of these three have a reader today, and that is a deliberate, verified
  // fact, not an oversight to fix later:
  //   - `label`: purely cosmetic, nothing downstream branches on it.
  //   - `capacity`: SAM-198's own decision (see this file's header comment and
  //     `operating_windows.py`'s module docstring) is that capacity stays UNIFORM
  //     across every window of a service — `settings.intervals.max_per_slot` is the
  //     single source of truth `lambda_function.py` enforces everywhere. The booking
  //     engine's own `PostRecurringScheduleSBlockSchema` (sam-booking-service
  //     `src/forms/RecurringScheduleForm.ts`) has no capacity-like field on a
  //     RecurringScheduleScheduleBlock either — there is nowhere to forward a
  //     per-window number even if SAM wanted to. Sending one would be silently
  //     dropped by the engine's zod schema (unknown keys aren't `.strict()`), so it
  //     is intentionally NOT sent — see `engine_sync.py::_sync_weekday_blocks`.
  //   - `instructor`: same story — no engine field, no SAM read site.
  //
  // Kept anyway (additive, optional, round-tripped verbatim by
  // `buildDayWindowsForSave`/`sortWindows` since both operate on whole window
  // objects) so an admin can annotate a session with what it's actually for. A
  // future consumer (a per-instructor view, a real per-session capacity field) can
  // start reading these without a payload-shape migration.
  label?: string;
  capacity?: string;
  instructor?: string;
}

/**
 * `close === open` is allowed (touching edges, e.g. 09:00 close / 09:00
 * open on the next window) — only `close < open` (or equal-but-empty) is a
 * hard error. Times are plain "HH:MM" strings; string comparison works
 * because they're all zero-padded and same length.
 */
export function isWindowOrderValid(window: TimeWindow): boolean {
  if (!window.open || !window.close) return false;
  return window.close > window.open;
}

/** True when a window's close is not after its open — a midnight crossing (or a blank/invalid window). */
export function crossesMidnight(window: TimeWindow): boolean {
  if (!window.open || !window.close) return false;
  return window.close <= window.open;
}

/** Two windows "overlap" when they share more than a touching edge. */
export function windowsOverlap(a: TimeWindow, b: TimeWindow): boolean {
  return a.open < b.close && b.open < a.close;
}

export interface DayWindowsValidationResult {
  valid: boolean;
  /** One message per problem found, in a stable order — first window-order errors, then overlaps. */
  errors: string[];
}

/**
 * Validates one weekday's full set of windows: `windows[0]` is the first
 * (existing) window, the rest are extras. Pure — no knowledge of `enabled`;
 * callers should skip disabled days.
 *
 * A day with a single window (no extras) is not validated at all — that's
 * exactly today's behaviour, and it's what lets an existing midnight-crossing
 * service (open 18:00, close 02:00, no extras) keep saving untouched.
 *
 * Once there's at least one extra, the first window may still cross
 * midnight — it's treated as [open, 24:00) for the same-day overlap check —
 * but an extra window may not: it must close after it opens.
 */
export function validateDayWindows(windows: TimeWindow[]): DayWindowsValidationResult {
  if (windows.length <= 1) {
    return { valid: true, errors: [] };
  }

  const errors: string[] = [];

  windows.forEach((w, i) => {
    if (i === 0) return; // the first window may cross midnight; never flagged here
    if (!isWindowOrderValid(w)) {
      errors.push(`Extra window ${i}: close time must be after open time.`);
    }
  });

  // Effective same-day interval per window, for the overlap check only.
  // The first window's midnight-crossing tail is capped at 24:00 — its
  // "tomorrow" portion isn't this day's concern. An extra with a bad order
  // is excluded (already reported above; its overlap status isn't meaningful).
  const effective: Array<TimeWindow | null> = windows.map((w, i) => {
    if (i === 0) {
      if (!w.open) return null;
      return { open: w.open, close: crossesMidnight(w) ? '24:00' : w.close };
    }
    return isWindowOrderValid(w) ? w : null;
  });

  for (let a = 0; a < effective.length; a += 1) {
    if (!effective[a]) continue;
    for (let b = a + 1; b < effective.length; b += 1) {
      if (!effective[b]) continue;
      if (windowsOverlap(effective[a]!, effective[b]!)) {
        errors.push('Time windows on this day overlap.');
      }
    }
  }

  return { valid: errors.length === 0, errors };
}

/** Sorts windows by open time, ascending. Does not mutate the input. */
export function sortWindows(windows: TimeWindow[]): TimeWindow[] {
  return [...windows].sort((a, b) => a.open.localeCompare(b.open));
}

export interface DayWindowsForSave {
  first: TimeWindow;
  extras: TimeWindow[];
}

/**
 * Reorders a day's windows for the save payload.
 *
 * Ordinarily the earliest window becomes the payload's `open`/`close` and
 * everything else — sorted — becomes `extra_windows`, so an extra added
 * earlier than the first window doesn't silently stay "extra".
 *
 * EXCEPT when the first window crosses midnight (e.g. 18:00 -> 02:00): its
 * `open` isn't the earliest clock time of the day, but it's still correctly
 * the first/anchor window (mirroring `weekday_windows()`'s tail/head split),
 * so it's left in place untouched and only the extras are sorted among
 * themselves.
 */
export function buildDayWindowsForSave(first: TimeWindow, extras: TimeWindow[]): DayWindowsForSave {
  const validExtras = extras.filter((w) => w.open && w.close);

  if (crossesMidnight(first)) {
    return { first, extras: sortWindows(validExtras) };
  }

  const [newFirst, ...rest] = sortWindows([first, ...validExtras]);
  return { first: newFirst, extras: rest };
}
